import { commonRequestData, fetchData } from "../../api";

/**
 * Valida los eventos del calendario de un hotel dentro de un rango de fechas específico.
 *
 * @param {Object} params - Parámetros necesarios para la validación.
 * @param {number} params.hotelid - Identificador único del hotel a validar.
 * @param {number} params.salonid - Identificador único del salón a validar.
 * @param {string} params.fi - Fecha inicial del rango en formato ISO (YYYY-MM-DD).
 * @param {string} params.fn - Fecha final del rango en formato ISO (YYYY-MM-DD).
 * @returns {Promise<Object>} Respuesta de la API con el resultado de la validación.
 *
 * @example
 * const response = await validarEventosCalendario({ hotelid: 1, salonid: 2, fi: "2024-01-01", fn: "2024-01-10" });
 * console.log(response); // Maneja la respuesta de la API
 */
export const validarEventosCalendario = async ({ hotelid, salonid, fi, fn }) => {
    // Construcción del payload para la solicitud a la API.
    // Se combinan datos comunes y específicos de la operación.
    const requestData = {
        ...commonRequestData, // Parámetros compartidos entre múltiples solicitudes (e.g., tokens, IDs generales).
        hotelid, // Identificador del hotel.
        salonid, // Identificador del salón asociado al evento.
        fi, // Fecha de inicio del intervalo a validar.
        fn, // Fecha de fin del intervalo a validar.
    };

    try {
        // Llamada a la API al endpoint correspondiente.
        // Este endpoint es responsable de validar eventos existentes en el calendario.
        const response = await fetchData("calendarios/validarEventosCalendario", requestData);

        // Se espera que la API devuelva datos relevantes para confirmar disponibilidad o conflictos.
        return response;
    } catch (error) {
        // Manejo de errores en caso de que falle la solicitud.
        console.error("Error al validar eventos del calendario:", error);
        throw new Error("No se pudo validar los eventos del calendario. Por favor, intente nuevamente.");
    }
};

/**
 * Inserta un nuevo evento en el calendario de un hotel.
 *
 * @param {Object} params - Parámetros necesarios para la inserción del evento.
 * @param {Object} params.evento - Datos del evento a registrar (debe cumplir con el esquema esperado por la API).
 * @param {string} params.token - Token de autenticación del usuario que realiza la acción.
 * @param {boolean} params.recolectarDaros - Bandera para determinar si se deben recolectar más datos tras la inserción.
 * @returns {Promise<Object>} Respuesta de la API con el resultado de la operación.
 *
 * @example
 * const response = await InsertarNuevoEvento({ evento: { ... }, token: "12345", recolectarDaros: true });
 * console.log(response); // Maneja la respuesta de la API
 */
export const InsertarNuevoEvento = async ({ evento, token, recolectarDaros, id_admin }) => {
    // Construcción del payload para la solicitud a la API.
    // Se combinan datos comunes y específicos para el registro del evento.
    const requestData = {
        ...commonRequestData, // Parámetros compartidos (e.g., autenticación o metadatos comunes).
        evento, // Datos específicos del evento que se desea insertar.
        token, // Token de autenticación del usuario.
        recolectarDaros, // Indicador para definir el comportamiento post-inserción.
        id_admin, // Identificador único del administrador que realiza la acción.
    };

    try {
        // Llamada a la API al endpoint correspondiente.
        // Este endpoint gestiona la inserción de nuevos eventos en el calendario.
        const response = await fetchData("calendarios/InsertarNuevoEvento", requestData);

        // Se espera que la API confirme el éxito o falle si hubo conflictos en la operación.
        return response;
    } catch (error) {
        // Manejo de errores en caso de que falle la solicitud.
        console.error("Error al insertar un nuevo evento en el calendario:", error);
        throw new Error("No se pudo insertar el evento en el calendario. Por favor, intente nuevamente.");
    }
};


export const obtenerLosDatosEventos = async ({ id }) => {
    // Construcción del payload para la solicitud a la API.
    // Combina parámetros comunes (e.g., autenticación) y específicos (ID del evento).
    const requestData = {
        ...commonRequestData, // Datos comunes necesarios para las solicitudes a la API.
        id, // Identificador único del evento que se desea consultar.
    };

    try {
        // Realiza una solicitud a la API al endpoint que gestiona la obtención de datos de eventos.
        const response = await fetchData("calendarios/obtener_EventoEspesifico", requestData);

        // Retorna la respuesta de la API que contiene los datos solicitados.
        return response;
    } catch (error) {
        // Captura y registra cualquier error ocurrido durante la solicitud.
        console.error("Error al obtener los datos del evento desde el calendario:", error);

        // Lanza un error con un mensaje claro para el manejo en niveles superiores.
        throw new Error("No se pudieron obtener los datos del evento. Por favor, intente nuevamente.");
    }
};




export const eliminar_EventosReasignacion = async ({ token }) => {
    // Construcción del payload para la solicitud a la API.
    // Combina datos comunes (e.g., autenticación) y parámetros específicos necesarios para la operación.
    const requestData = {
        ...commonRequestData, // Información estándar requerida por la API.
        token, // Token de autenticación necesario para validar la solicitud.
    };

    try {
        // Realiza una solicitud a la API en el endpoint correspondiente para eliminar eventos de reasignación.
        const response = await fetchData("calendarios/eliminar_EventosReasignacion", requestData);

        // Retorna la respuesta de la API, que puede contener información relevante del resultado.
        return response;
    } catch (error) {
        // Manejo de errores en caso de que la solicitud falle.
        console.error("Error al eliminar los eventos de reasignación desde el calendario:", error);

        // Lanza un error con un mensaje claro para que pueda ser manejado en niveles superiores.
        throw new Error("No se pudieron eliminar los eventos de reasignación. Por favor, intente nuevamente.");
    }
};


export const cancelar_EventosReasignacion = async ({ token }) => {
    // Construye el payload para la solicitud a la API, combinando datos comunes
    // (e.g., información estándar de autenticación) con parámetros específicos necesarios para la operación.
    const requestData = {
        ...commonRequestData, // Datos estándar requeridos por la API (e.g., encabezados, autenticación, etc.).
        token, // Token de autenticación para validar la solicitud.
    };

    try {
        // Envía una solicitud al endpoint de la API para cancelar eventos de reasignación en el calendario.
        const response = await fetchData("calendarios/cancelar_EventosReasignacion", requestData);

        // Retorna la respuesta de la API, que puede incluir detalles relevantes sobre la operación.
        return response;
    } catch (error) {
        // Registra el error en la consola para facilitar el diagnóstico.
        console.error("Error al cancelar los eventos de reasignación desde el calendario:", error);

        // Lanza un error con un mensaje descriptivo para que sea manejado en niveles superiores.
        throw new Error("No se pudieron cancelar los eventos de reasignación. Por favor, intente nuevamente.");
    }
};


export const EXTARERTODOSOSVENTOSTOKEN = async ({ token }) => {
    // Construye el payload para la solicitud a la API, combinando datos comunes
    // (como encabezados o datos de autenticación) con los parámetros específicos necesarios para esta operación.
    const requestData = {
        ...commonRequestData, // Datos estándar requeridos para interactuar con la API.
        token, // Token necesario para identificar y validar la solicitud.
    };

    try {
        // Realiza una solicitud al endpoint de la API para obtener eventos asociados al token en el calendario.
        const response = await fetchData("calendarios/datosEventoToken", requestData);

        // Devuelve la respuesta obtenida de la API, la cual puede incluir los eventos o información relevante.
        return response;
    } catch (error) {
        // Registra el error en la consola para ayudar en el diagnóstico y depuración.
        console.error("Error al obtener los eventos asociados al token desde el calendario:", error);

        // Lanza un error descriptivo para permitir el manejo adecuado en niveles superiores.
        throw new Error("No se pudieron obtener los eventos asociados. Por favor, intente nuevamente.");
    }
};


